import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useDropboxFolders } from './hooks/useDropboxFolders';
import { useDropboxFiles } from './hooks/useDropboxFiles';
import { useTags } from './hooks/useTags';
import { useRecentActivity } from './hooks/useRecentActivity';
import { setCurrentFolder, setStorageUsage, setSortBy, setSortDirection } from '../../store/reducers/dropbox';
import * as dropboxService from '../../services/dropbox';
import DropboxLayout from './components/Layout/DropboxLayout';
import DropboxSidebarPro from './components/Layout/DropboxSidebarPro';
import DropboxHeaderNew from './components/Layout/DropboxHeaderNew';
import FileListModern from './components/FileList/FileListModern';
import FileGridModern from './components/FileList/FileGridModern';
import ContextMenu from './components/FileList/ContextMenu';
import EmptyState from './components/FileList/EmptyState';
import NewFolderModal from './components/Modals/NewFolderModal';
import UploadFileModal from './components/Modals/UploadFileModal';
import MoveFileModal from './components/Modals/MoveFileModal';
import MoveFolderModal from './components/Modals/MoveFolderModal';
import DeleteConfirmModal from './components/Modals/DeleteConfirmModal';
import TagsModal from './components/Modals/TagsModal';
import DetailsModal from './components/Modals/DetailsModal';
import RenameModal from './components/Modals/RenameModal';
import PreviewModal from './components/Modals/PreviewModal';
import CopyModal from './components/Modals/CopyModal';
import BulkCopyModal from './components/Modals/BulkCopyModal';
import BulkMoveModal from './components/Modals/BulkMoveModal';
import MetadataModal from './components/Modals/MetadataModal';
import ConfirmDialog from './components/Modals/ConfirmDialog';
import SelectionActionBar from './components/Layout/SelectionActionBar';
import './dropbox.css';
import './dropbox-modern.css';
import './dropbox-header-new.css';
import './selection-action-bar.css';
import './dropbox-pro.css';

const DropboxPage: React.FC = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const { currentFolderId, folders, files, viewMode, loading, searchQuery, selectedTags, recentActivity } = useSelector(
    (state: any) => state.dropbox
  );

  // Role-based permissions
  const canAccess = useSelector((state: any) => state.auth.permissions?.opsmanual);

  // Check if user has list permission
  if (!canAccess?.list) {
    return (
      <div className="dropbox-page">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>Dropbox / Ops Manual</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li className="breadcrumb-item active">Ops Manual</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              You do not have permission to access the Ops Manual.
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Custom hooks
  const { fetchFolders } = useDropboxFolders();
  const { fetchFiles } = useDropboxFiles();
  const { fetchAllTags } = useTags();
  const { fetchRecentActivity, trackFileAccess } = useRecentActivity();

  // Local state for modals
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMoveFileModal, setShowMoveFileModal] = useState(false);
  const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);
  const [bulkDownloadUrls, setBulkDownloadUrls] = useState<any[]>([]);
  const [showBulkCopyModal, setShowBulkCopyModal] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [metadataItem, setMetadataItem] = useState<any>(null);
  const [metadataItemType, setMetadataItemType] = useState<'file' | 'folder'>('file');
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [renameItemType, setRenameItemType] = useState<'file' | 'folder'>('file');
  const [copyItemType, setCopyItemType] = useState<'file' | 'folder'>('file');
  const [currentView, setCurrentView] = useState<'all' | 'favorites' | 'recent' | 'trash'>('all');
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    show: boolean;
    x: number;
    y: number;
    item: any;
    type: 'file' | 'folder';
  }>({ show: false, x: 0, y: 0, item: null, type: 'file' });

  // Load initial data
  useEffect(() => {
    loadData();
  }, [currentFolderId, currentView]);

  // Clear selection when folder changes
  useEffect(() => {
    handleClearSelection();
  }, [currentFolderId]);

  const loadData = async () => {
    try {
      const includeDeleted = currentView === 'trash';
      const folderParam = currentView === 'all' ? currentFolderId || undefined : undefined;
      const starredOnly = currentView === 'favorites' ? true : undefined;
      
      // For Recent tab, fetch recent activity FIRST before files
      if (currentView === 'recent') {
        await fetchRecentActivity({ limit: 20 });
      }
      
      // For Recent tab, we need to fetch ALL files globally, not folder-specific
      const fetchParams: any = {
        include_deleted: includeDeleted,
        starred_only: starredOnly
      };
      
      // Only add folder_id for 'all' view, exclude it for recent/favorites/trash to get global results
      if (currentView === 'all') {
        fetchParams.folder_id = folderParam;
      } else if (currentView === 'recent') {
        // For recent view, explicitly request global files
        fetchParams.global_only = true;
      }
      
      // Fetch folders, files, and tags
      await Promise.all([
        fetchFolders({ include_deleted: includeDeleted }),
        fetchFiles(fetchParams),
        fetchAllTags()
      ]);
      
      const storageResponse = await dropboxService.getStorageUsage();
      if (storageResponse?.success) {
        dispatch(setStorageUsage(storageResponse.data));
      }
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleFolderClick = (folderId: string | null) => {
    dispatch(setCurrentFolder(folderId));
  };

  const handleNewFolder = () => {
    if (!canAccess?.['create-new-folder']) {
      toast.error('You do not have permission to create folders');
      return;
    }
    setShowNewFolderModal(true);
  };

  const handleUpload = () => {
    if (!canAccess?.['upload-document']) {
      toast.error('You do not have permission to upload documents');
      return;
    }
    setShowUploadModal(true);
  };

  const handleMoveFile = (file: any) => {
    if (!canAccess?.move) {
      toast.error('You do not have permission to move files');
      return;
    }
    setSelectedItem(file);
    setShowMoveFileModal(true);
  };

  const handleMoveFolder = (folder: any) => {
    if (!canAccess?.move) {
      toast.error('You do not have permission to move folders');
      return;
    }
    setSelectedItem(folder);
    setShowMoveFolderModal(true);
  };

  const handleDelete = (item: any) => {
    if (!canAccess?.delete) {
      toast.error('You do not have permission to delete items');
      return;
    }
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleRestoreFile = async (file: any) => {
    if (!canAccess?.['restore-document']) {
      toast.error('You do not have permission to restore files');
      return;
    }
    try {
      const response = await dropboxService.restoreFile(file.id);
      if (response?.success) {
        toast.success('File restored successfully');
        loadData();
      } else {
        toast.error(response?.message || 'Failed to restore file');
      }
    } catch (error) {
      toast.error('Failed to restore file');
    }
  };

  const handleRestoreFolder = async (folder: any) => {
    if (!canAccess?.['restore-document']) {
      toast.error('You do not have permission to restore folders');
      return;
    }
    try {
      const response = await dropboxService.restoreFolder(folder.id);
      if (response?.success) {
        toast.success('Folder restored successfully');
        loadData();
      } else {
        toast.error(response?.message || 'Failed to restore folder');
      }
    } catch (error) {
      toast.error('Failed to restore folder');
    }
  };

  const handleShowDetails = (item: any, type: 'file' | 'folder') => {
    setSelectedItem({ ...item, itemType: type });
    setShowDetailsModal(true);
  };

  const handleRename = (item: any, type: 'file' | 'folder') => {
    if (!canAccess?.rename) {
      toast.error('You do not have permission to rename items');
      return;
    }
    setSelectedItem(item);
    setRenameItemType(type);
    setShowRenameModal(true);
  };

  const handlePreview = async (file: any) => {
    if (!canAccess?.['preview-document']) {
      toast.error('You do not have permission to preview documents');
      return;
    }
    
    // Don't track here - PreviewModal will track when file loads successfully
    setSelectedItem(file);
    setShowPreviewModal(true);
  };

  const handleCopy = (item: any, type: 'file' | 'folder') => {
    if (!canAccess?.copy) {
      toast.error('You do not have permission to copy items');
      return;
    }
    setSelectedItem(item);
    setCopyItemType(type);
    setShowCopyModal(true);
  };

  const handleViewChange = (view: 'all' | 'favorites' | 'recent' | 'trash') => {
    setCurrentView(view);
    // Reset folder context for all views to ensure clean state
    dispatch(setCurrentFolder(null));
    handleClearSelection();
  };

  const handleJumpBack = () => {
    if (currentFolderId) {
      const currentFolder = folders.find((f: any) => f.id === currentFolderId);
      if (currentFolder && currentFolder.parent_id) {
        dispatch(setCurrentFolder(currentFolder.parent_id));
      } else {
        dispatch(setCurrentFolder(null)); // Go to root
      }
      handleClearSelection();
    }
  };

  const handleFavouriteFile = async (file: any) => {
    try {
      if (file.is_starred) {
        await dropboxService.unstarFile(file.id);
        toast.success('Removed from favourites');
      } else {
        await dropboxService.starFile(file.id);
        toast.success('Added to favourites');
      }
      loadData();
    } catch (error) {
      toast.error('Failed to update favourite status');
    }
  };

  const handleDownloadFile = async (file: any) => {
    if (!canAccess?.download) {
      toast.error('You do not have permission to download files');
      return;
    }
    try {
      toast.info('Preparing download...');
      const response = await dropboxService.downloadFile(file.id);
      
      if (response?.success && response?.data?.downloadUrl) {
        // Use the filename from API response or fallback to file.name
        const fileName = response.data.fileName || file.name || 'download';
        
        // Create a proper download link
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = fileName;
        link.style.display = 'none';
        link.target = '_blank';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Track download activity
        // try {
        //   await trackFileAccess(file.id, 'downloaded');
        //   
        //   // Refresh recent activity if we're in recent view
        //   if (currentView === 'recent') {
        //     await fetchRecentActivity({ limit: 20 });
        //   }
        // } catch (trackError) {
        //   // Tracking error - fail silently
        // }
        
        toast.success(`Download started: ${fileName}`);
      } else {
        toast.error('Failed to download file');
      }
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleMetadata = (item: any, type: 'file' | 'folder') => {
    if (!canAccess?.['edit-metadata']) {
      toast.error('You do not have permission to edit metadata');
      return;
    }
    setMetadataItem(item);
    setMetadataItemType(type);
    setShowMetadataModal(true);
  };

  const handleContextMenu = (item: any, type: 'file' | 'folder', event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({
      show: true,
      x: event.clientX,
      y: event.clientY,
      item,
      type
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ ...contextMenu, show: false });
  };

  // Handle selection changes from FileListModern
  const handleSelectionChange = useCallback((ids: string[], items: any[]) => {
    setSelectedIds(ids);
    setSelectedItems(items);
  }, []);

  // Determine selection type
  const getSelectionType = (): 'files' | 'folders' | 'mixed' => {
    const hasFiles = selectedItems.some(item => files.find((f: any) => f.id === item.id));
    const hasFolders = selectedItems.some(item => folders.find((f: any) => f.id === item.id));
    
    if (hasFiles && hasFolders) return 'mixed';
    if (hasFiles) return 'files';
    return 'folders';
  };

  // Helper function to download multiple files sequentially
  const downloadMultipleFiles = async (downloadUrls: any[]) => {
    let downloadedCount = 0;
    let failedCount = 0;
    
    // Show initial progress
    toast.info(`Starting download of ${downloadUrls.length} files...`);
    
    // Function to download a single file with retry logic
    const downloadSingleFile = (item: any, index: number): Promise<boolean> => {
      return new Promise((resolve) => {
        try {
          // Method 1: Direct link method (avoids CORS issues with S3 signed URLs)
          try {
            const link = document.createElement('a');
            link.href = item.url;
            link.download = item.name || `file_${index + 1}`;
            link.style.display = 'none';
            link.target = '_blank';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            resolve(true);
          } catch (directError) {
            // Method 2: Fallback - open in new window
            try {
              window.open(item.url, '_blank');
              resolve(true);
            } catch (windowError) {
              resolve(false);
            }
          }
          
        } catch (error) {
          resolve(false);
        }
      });
    };
    
    // Check if browser might block multiple downloads
    const userAgent = navigator.userAgent.toLowerCase();
    const isChrome = userAgent.includes('chrome');
    const isFirefox = userAgent.includes('firefox');
    
    if (downloadUrls.length > 5 && (isChrome || isFirefox)) {
      // For many files, warn user about browser restrictions
      toast.warning(
        `Downloading ${downloadUrls.length} files. Your browser may block some downloads. ` +
        `Please allow multiple downloads when prompted, or download files in smaller batches.`,
        { autoClose: 8000 }
      );
    }
    
    // Download files with staggered timing to avoid browser blocking
    for (let i = 0; i < downloadUrls.length; i++) {
      const item = downloadUrls[i];
      
      // Show progress for each file (but don't spam with too many notifications)
      if (downloadUrls.length <= 5 || i % 3 === 0 || i === downloadUrls.length - 1) {
        toast.info(`Downloading ${item.name} (${i + 1}/${downloadUrls.length})...`, {
          autoClose: 3000
        });
      }
      
      try {
        const success = await downloadSingleFile(item, i);
        
        if (success) {
          downloadedCount++;
        } else {
          failedCount++;
        }
        
        // Wait between downloads to avoid browser restrictions
        // Longer delay for more files to be more browser-friendly
        if (i < downloadUrls.length - 1) {
          const delay = downloadUrls.length > 10 ? 2000 : 1500;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
      } catch (error) {
        failedCount++;
      }
    }
    
    // Show final result with helpful information
    
    if (failedCount === 0) {
      toast.success(`Successfully downloaded all ${downloadedCount} files! Check your Downloads folder.`);
    } else if (downloadedCount > 0) {
      toast.warning(
        `Downloaded ${downloadedCount} files, ${failedCount} may have failed. ` +
        `Check your browser's download settings and Downloads folder. ` +
        `Some browsers block multiple downloads - try downloading in smaller batches if needed.`,
        { autoClose: 12000 }
      );
    } else {
      // If all downloads failed, offer manual download option
      toast.error(
        'Automatic download failed. Opening manual download option...',
        { autoClose: 5000 }
      );
      
      // Show manual download modal
      setBulkDownloadUrls(downloadUrls);
      setShowBulkDownloadModal(true);
    }
  };

  // Selection action handlers
  const handleBulkDownload = async () => {
    if (!canAccess?.['bulk-download']) {
      toast.error('You do not have permission to bulk download');
      return;
    }
    if (selectedIds.length === 0) return;
    
    try {
      const items = selectedItems.map(item => ({
        id: item.id,
        type: files.find((f: any) => f.id === item.id) ? 'file' as const : 'folder' as const
      }));

      toast.info('Preparing downloads...');
      
      const response = await dropboxService.bulkDownload(items);
      if (response?.success) {
        // Track file downloads
        // const fileItems = items.filter(item => item.type === 'file');
        // for (const file of fileItems) {
        //   try {
        //     await trackFileAccess(file.id, 'downloaded');
        //   } catch (trackError) {
        //     // Tracking error - fail silently
        //   }
        // }
        
        // Refresh recent activity if we're in recent view
        // if (currentView === 'recent' && fileItems.length > 0) {
        //   await fetchRecentActivity({ limit: 20 });
        // }
        
        if (response.data?.downloadUrls && response.data.downloadUrls.length > 0) {
          const downloadUrls = response.data.downloadUrls;
          
          // For multiple files, we need to handle browser download restrictions
          if (downloadUrls.length === 1) {
            // Single file - direct download
            const item = downloadUrls[0];
            const link = document.createElement('a');
            link.href = item.url;
            link.download = item.name || 'download';
            link.style.display = 'none';
            link.target = '_blank';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            toast.success('Download started');
          } else {
            // Multiple files - use improved sequential download with user interaction
            // Show helpful tip for first-time bulk download users
            if (downloadUrls.length > 3) {
              toast.info(
                `💡 Tip: For ${downloadUrls.length} files, your browser may ask permission for multiple downloads. ` +
                `Click "Allow" when prompted to download all files.`,
                { autoClose: 6000 }
              );
            }
            
            // Try automatic download first
            await downloadMultipleFiles(downloadUrls);
            
            // If user wants manual control, we could add a manual download option
            // This would be useful if automatic downloads fail
          }
        } else if (response.data?.downloadUrl) {
          // Single zip file
          const link = document.createElement('a');
          link.href = response.data.downloadUrl;
          link.download = 'download.zip';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('Download started');
        } else {
          toast.warning('No files available for download');
        }
      } else {
        toast.error(response?.message || 'Failed to download items');
      }
    } catch (error) {
      toast.error('Failed to download items');
    }
  };

  const handleBulkMove = () => {
    if (!canAccess?.['bulk-move']) {
      toast.error('You do not have permission to bulk move');
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }
    setShowBulkMoveModal(true);
  };

  const handleBulkCopy = () => {
    if (!canAccess?.['bulk-copy']) {
      toast.error('You do not have permission to bulk copy');
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }
    setShowBulkCopyModal(true);
  };

  const handleBulkDelete = async () => {
    if (!canAccess?.['bulk-delete']) {
      toast.error('You do not have permission to bulk delete');
      return;
    }
    if (selectedIds.length === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkRestore = async () => {
    if (!canAccess?.['restore-document']) {
      toast.error('You do not have permission to restore items');
      return;
    }
    if (selectedIds.length === 0) return;
    
    try {
      // selectedItems already have the type field from FileListModern/FileGridModern
      const items = selectedItems.map(item => ({
        id: item.id,
        type: item.type || (files.find((f: any) => f.id === item.id) ? 'file' as const : 'folder' as const)
      }));

      const response = await dropboxService.bulkRestore(items);
      
      if (response?.success) {
        toast.success(`${response.data.restored} item(s) restored successfully`);
        if (response.data.failed > 0) {
          toast.warning(`${response.data.failed} item(s) failed to restore`);
        }
        handleClearSelection();
        loadData();
      } else {
        toast.error('Failed to restore items');
      }
    } catch (error) {
      toast.error('Failed to restore items');
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      const items = selectedItems.map(item => ({
        id: item.id,
        type: files.find((f: any) => f.id === item.id) ? 'file' as const : 'folder' as const
      }));

      const isTrashView = currentView === 'trash';
      const response = await dropboxService.bulkDelete(items, isTrashView);
      if (response?.success) {
        toast.success(
          isTrashView
            ? `${items.length} item(s) permanently deleted`
            : `${items.length} item(s) moved to trash`
        );
        handleClearSelection();
        loadData();
      } else {
        toast.error('Failed to delete items');
      }
    } catch (error) {
      toast.error('Failed to delete items');
    } finally {
      setBulkDeleteLoading(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  const handleBulkFavourite = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      const fileIds = selectedItems
        .filter(item => files.find((f: any) => f.id === item.id))
        .map(item => item.id);

      if (fileIds.length === 0) {
        toast.info('Only files can be added to favourites');
        return;
      }

      // Check if we're in favourites view - if so, remove from favourites
      if (currentView === 'favorites') {
        // For favourites view, we need to remove from favourites
        const response = await dropboxService.bulkUnstar(fileIds);
        
        if (response?.success) {
          toast.success(response.message || `${fileIds.length} file(s) removed from favourites`);
        } else {
          const successCount = response?.data?.successCount || 0;
          const failCount = response?.data?.failCount || fileIds.length;
          
          if (successCount > 0) {
            toast.success(`${successCount} file(s) removed from favourites`);
          }
          if (failCount > 0) {
            toast.error(`Failed to remove ${failCount} file(s) from favourites`);
          }
        }
      } else {
        // For other views, add to favourites
        const response = await dropboxService.bulkStar(fileIds);
        if (response?.success) {
          toast.success(`${fileIds.length} file(s) added to favourites`);
        } else {
          toast.error(response?.message || 'Failed to add files to favourites');
        }
      }
      
      handleClearSelection();
      loadData();
    } catch (error) {
      toast.error('Failed to update favourite status');
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectedItems([]);
  };

  // Filter files based on current view
  const getFilteredFiles = () => {
    let filteredFiles = files;
    
    if (currentView === 'all') {
      filteredFiles = files.filter((f: any) => f.folder_id === currentFolderId && !f.is_deleted);
    } else if (currentView === 'favorites') {
      // For favorites, the API should already return only starred files
      // But we still need to filter out deleted ones
      filteredFiles = files.filter((f: any) => f.is_starred && !f.is_deleted);
    } else if (currentView === 'recent') {
      // For recent view, use recentActivity data to show files that were actually accessed
      if (recentActivity && recentActivity.length > 0) {
        // Sort recent activity by accessed_at (most recent first)
        const sortedActivity = [...recentActivity]
          .filter((activity: any) => {
            // Filter for files only and not deleted
            const isFile = activity.entity_type === 'file';
            const hasEntity = activity.entity && !activity.entity.is_deleted;
            return isFile && hasEntity;
          })
          .sort((a: any, b: any) => {
            const aDate = new Date(a.accessed_at || 0).getTime();
            const bDate = new Date(b.accessed_at || 0).getTime();
            return bDate - aDate; // Most recent access first
          });
        
        // Use entity data directly from recent API - it has all the fields we need
        filteredFiles = sortedActivity
          .map((activity: any) => {
            if (activity.entity) {
              // Entity has mime_type, size_bytes, name, etc.
              const fileData = { 
                ...activity.entity,
                accessed_at: activity.accessed_at,
                action: activity.action,
                // Ensure size_bytes is a number (API might return string)
                size_bytes: typeof activity.entity.size_bytes === 'string' 
                  ? parseInt(activity.entity.size_bytes, 10) 
                  : activity.entity.size_bytes,
                // Extract extension from name if not provided
                extension: activity.entity.extension || activity.entity.name?.split('.').pop()
              };
              
              return fileData;
            }
            return null;
          })
          .filter((f: any) => f !== null); // Remove null entries
      } else {
        // No recent activity data - show empty
        filteredFiles = [];
      }
    } else if (currentView === 'trash') {
      filteredFiles = files.filter((f: any) => f.is_deleted);
    }
    
    // Apply search query filter
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredFiles = filteredFiles.filter((f: any) => 
        f.name.toLowerCase().includes(query) ||
        (f.extension && f.extension.toLowerCase().includes(query)) ||
        (f.tags && f.tags.some((tag: string) => tag.toLowerCase().includes(query)))
      );
    }
    
    // Apply tags filter
    if (selectedTags && selectedTags.length > 0) {
      filteredFiles = filteredFiles.filter((f: any) => 
        f.tags && selectedTags.some((tag: string) => f.tags.includes(tag))
      );
    }
    
    return filteredFiles;
  };

  const currentFolder = folders.find((f: any) => f.id === currentFolderId);
  const currentFiles = getFilteredFiles();
  const currentFolders = currentView === 'all' 
    ? folders.filter((f: any) => f.parent_id === currentFolderId)
    : currentView === 'trash'
    ? folders.filter((f: any) => f.is_deleted)
    : [];

  return (
    <div className="dropbox-page dropbox-container">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Ops Manual</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item active">Ops Manual</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <DropboxLayout
            sidebar={<DropboxSidebarPro onFolderClick={handleFolderClick} onViewChange={handleViewChange} onFolderMoved={loadData} currentView={currentView} />}
            onViewChange={handleViewChange}
            main={
              <div className="dropbox-modern-container">
                {/* New Header */}
                <DropboxHeaderNew
                  currentFolderName={currentFolder?.name || 'All Files'}
                  onNewFolder={handleNewFolder}
                  onUpload={handleUpload}
                  onViewChange={handleViewChange}
                  currentView={currentView}
                  folders={folders}
                  currentFolderId={currentFolderId}
                  onFolderClick={handleFolderClick}
                />

                {/* Tabs or Selection Action Bar */}
                {selectedIds.length > 0 ? (
                  <SelectionActionBar
                    selectedCount={selectedIds.length}
                    selectionType={getSelectionType()}
                    isTrashView={currentView === 'trash'}
                    isFavouritesView={currentView === 'favorites'}
                    onDownload={getSelectionType() === 'files' && canAccess?.['bulk-download'] ? handleBulkDownload : undefined}
                    onCopy={canAccess?.['bulk-copy'] ? handleBulkCopy : () => toast.error('You do not have permission to bulk copy')}
                    onMove={canAccess?.['bulk-move'] ? handleBulkMove : () => toast.error('You do not have permission to bulk move')}
                    onDelete={canAccess?.['bulk-delete'] ? handleBulkDelete : () => toast.error('You do not have permission to bulk delete')}
                    onRestore={canAccess?.['restore-document'] ? handleBulkRestore : undefined}
                    onStar={getSelectionType() === 'files' ? handleBulkFavourite : undefined}
                    onClearSelection={handleClearSelection}
                  />
                ) : (
                  <div className="dropbox-tabs">
                    <button 
                      className="dropbox-tab"
                      onClick={handleJumpBack}
                      disabled={!currentFolderId}
                      title="Go to parent folder"
                    >
                      <i className="fas fa-arrow-left"></i> Jump Back
                    </button>
                    <button 
                      className={`dropbox-tab ${currentView === 'all' && !currentFolderId ? 'active' : ''}`}
                      onClick={() => handleViewChange('all')}
                    >
                      <i className="fas fa-folder-open"></i> All Files
                    </button>
                    <button 
                      className={`dropbox-tab ${currentView === 'recent' ? 'active' : ''}`}
                      onClick={() => handleViewChange('recent')}
                    >
                      <i className="fas fa-clock"></i> Recents
                    </button>
                    <button 
                      className={`dropbox-tab ${currentView === 'favorites' ? 'active' : ''}`}
                      onClick={() => handleViewChange('favorites')}
                    >
                      <i className="fas fa-star"></i> Favourites
                    </button>
                    <button 
                      className={`dropbox-tab ${currentView === 'trash' ? 'active' : ''}`}
                      onClick={() => handleViewChange('trash')}
                    >
                      <i className="fas fa-trash"></i> Trash
                    </button>
                  </div>
                )}

                {/* Scrollable Content Area */}
                <div className="dropbox-content-scrollable">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  ) : currentFiles.length === 0 && currentFolders.length === 0 ? (
                    <EmptyState 
                      onNewFolder={handleNewFolder} 
                      onUpload={handleUpload} 
                    />
                  ) : viewMode === 'grid' ? (
                    <FileGridModern
                      folders={currentFolders}
                      files={currentFiles}
                      onFolderClick={handleFolderClick}
                      onContextMenu={handleContextMenu}
                      onRefresh={loadData}
                      contextMenuItemId={contextMenu.show ? contextMenu.item?.id : null}
                      currentView={currentView}
                    />
                  ) : (
                    <FileListModern
                      folders={currentFolders}
                      files={currentFiles}
                      selectedIds={selectedIds}
                      onFolderClick={handleFolderClick}
                      onContextMenu={handleContextMenu}
                      onSelectionChange={handleSelectionChange}
                      onItemMoved={loadData}
                      contextMenuItemId={contextMenu.show ? contextMenu.item?.id : null}
                      currentView={currentView}
                    />
                  )}
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* Context Menu */}
      {contextMenu.show && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          item={contextMenu.item}
          type={contextMenu.type}
          onClose={closeContextMenu}
          onOpen={() => {
            if (contextMenu.type === 'folder') {
              handleFolderClick(contextMenu.item.id);
            }
          }}
          onRename={canAccess?.rename ? () => handleRename(contextMenu.item, contextMenu.type) : undefined}
          onCopy={canAccess?.copy ? () => handleCopy(contextMenu.item, contextMenu.type) : undefined}
          onMove={canAccess?.move ? () => {
            if (contextMenu.type === 'file') {
              handleMoveFile(contextMenu.item);
            } else {
              handleMoveFolder(contextMenu.item);
            }
          } : undefined}
          onDownload={contextMenu.type === 'file' && canAccess?.download ? () => handleDownloadFile(contextMenu.item) : undefined}
          onPreview={contextMenu.type === 'file' && canAccess?.['preview-document'] ? () => handlePreview(contextMenu.item) : undefined}
          onShare={() => toast.info('Share coming soon')}
          onDelete={canAccess?.delete ? () => handleDelete(contextMenu.item) : undefined}
          onRestore={canAccess?.['restore-document'] ? () => {
            if (contextMenu.type === 'file') {
              handleRestoreFile(contextMenu.item);
            } else {
              handleRestoreFolder(contextMenu.item);
            }
          } : undefined}
          onStar={contextMenu.type === 'file' ? () => handleFavouriteFile(contextMenu.item) : undefined}
          onDetails={() => handleShowDetails(contextMenu.item, contextMenu.type)}
          onMetadata={canAccess?.['edit-metadata'] ? () => handleMetadata(contextMenu.item, contextMenu.type) : undefined}
        />
      )}

      {/* Modals */}
      {showNewFolderModal && (
        <NewFolderModal
          parentId={currentFolderId}
          onClose={() => setShowNewFolderModal(false)}
          onSuccess={loadData}
        />
      )}

      {showUploadModal && (
        <UploadFileModal
          folderId={currentFolderId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={loadData}
        />
      )}

      {showMoveFileModal && selectedItem && (
        <MoveFileModal
          file={selectedItem}
          onClose={() => {
            setShowMoveFileModal(false);
            setSelectedItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {showMoveFolderModal && selectedItem && (
        <MoveFolderModal
          folder={selectedItem}
          onClose={() => {
            setShowMoveFolderModal(false);
            setSelectedItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {showDeleteModal && selectedItem && (
        <DeleteConfirmModal
          item={selectedItem}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {showTagsModal && selectedItem && (
        <TagsModal
          file={selectedItem}
          onClose={() => {
            setShowTagsModal(false);
            setSelectedItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {showDetailsModal && selectedItem && (
        <DetailsModal
          item={selectedItem}
          type={selectedItem.itemType}
          folders={folders}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedItem(null);
          }}
        />
      )}

      {showRenameModal && selectedItem && (
        <RenameModal
          item={selectedItem}
          type={renameItemType}
          onClose={() => {
            setShowRenameModal(false);
            setSelectedItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {showPreviewModal && selectedItem && (
        <PreviewModal
          file={selectedItem}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedItem(null);
          }}
          // onPreviewTracked={async () => {
          //   // Refresh recent activity if we're in recent view
          //   if (currentView === 'recent') {
          //     await fetchRecentActivity({ limit: 20 });
          //   }
          // }}
        />
      )}

      {showCopyModal && selectedItem && (
        <CopyModal
          item={selectedItem}
          type={copyItemType}
          onClose={() => {
            setShowCopyModal(false);
            setSelectedItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {/* Metadata Modal */}
      {showMetadataModal && metadataItem && (
        <MetadataModal
          show={showMetadataModal}
          item={metadataItem}
          itemType={metadataItemType}
          onClose={() => {
            setShowMetadataModal(false);
            setMetadataItem(null);
          }}
          onSuccess={loadData}
        />
      )}

      {/* Bulk Copy Modal */}
      {showBulkCopyModal && (
        <BulkCopyModal
          selectedItems={selectedItems}
          onClose={() => {
            setShowBulkCopyModal(false);
          }}
          onSuccess={() => {
            handleClearSelection();
            loadData();
          }}
        />
      )}

      {/* Bulk Move Modal */}
      {showBulkMoveModal && (
        <BulkMoveModal
          selectedItems={selectedItems}
          onClose={() => {
            setShowBulkMoveModal(false);
          }}
          onSuccess={() => {
            handleClearSelection();
            loadData();
          }}
        />
      )}

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        show={showBulkDeleteConfirm}
        title="Delete Items"
        message={
          <>
            Are you sure you want to delete <strong>{selectedIds.length} item(s)</strong>?
            <div className="mt-2">
              <small className="text-muted">
                <i className="fas fa-info-circle mr-1"></i>
                {currentView === 'trash'
                  ? 'Items will be permanently deleted and cannot be restored'
                  : 'Items will be moved to trash and can be restored later'}
              </small>
            </div>
          </>
        }
        confirmText={currentView === 'trash' ? 'Delete Permanently' : 'Move to Trash'}
        cancelText="Cancel"
        confirmVariant={currentView === 'trash' ? 'danger' : 'warning'}
        onConfirm={confirmBulkDelete}
        onCancel={() => setShowBulkDeleteConfirm(false)}
        loading={bulkDeleteLoading}
        icon={<i className="fas fa-trash text-warning"></i>}
      />

      {/* Manual Bulk Download Modal */}
      {showBulkDownloadModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-download mr-2"></i>
                  Manual Download Required
                </h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => setShowBulkDownloadModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  Your browser blocked automatic downloads. Please click each download link below manually:
                </div>
                
                <div className="list-group">
                  {bulkDownloadUrls.map((item, index) => (
                    <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <i className="fas fa-file mr-2"></i>
                        <strong>{item.name}</strong>
                        {item.size && (
                          <small className="text-muted ml-2">
                            ({(parseInt(item.size) / 1024 / 1024).toFixed(1)} MB)
                          </small>
                        )}
                      </div>
                      <a
                        href={item.url}
                        download={item.name}
                        className="btn btn-primary btn-sm"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fas fa-download mr-1"></i>
                        Download
                      </a>
                    </div>
                  ))}
                </div>
                
                <div className="alert alert-warning mt-3">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <strong>Tips:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Right-click and "Save link as..." if direct download doesn't work</li>
                    <li>Check your browser's download settings if files don't appear</li>
                    <li>Some browsers may still block multiple downloads - download one at a time if needed</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowBulkDownloadModal(false)}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-info mr-2" 
                  onClick={() => {
                    // Open all download URLs in new tabs as last resort
                    bulkDownloadUrls.forEach((item, index) => {
                      setTimeout(() => {
                        window.open(item.url, '_blank');
                      }, index * 500);
                    });
                    toast.info('Opening all files in new tabs...');
                  }}
                >
                  <i className="fas fa-external-link-alt mr-2"></i>
                  Open All in New Tabs
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={() => {
                    // Try to download all files again automatically
                    downloadMultipleFiles(bulkDownloadUrls);
                    setShowBulkDownloadModal(false);
                  }}
                >
                  <i className="fas fa-redo mr-2"></i>
                  Try Auto Download Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropboxPage;
